import { reconnectDelayMs, type DisplayRequestResponse, type PairResponse } from '@bingo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api.js';
import { connectRealtime } from '../../lib/ws.js';

/**
 * Ciclo de vida do pareamento da TV.
 *
 * Guarda o display token no `localStorage` — e aqui isso é a escolha certa, não
 * um descuido: o token é **somente leitura**, escopado a um evento, revogável
 * pelo operador, e a TV precisa dele para reconectar sozinha depois de dias
 * ligada. É o oposto do access token do operador, que vive só em memória.
 *
 * Fluxo: token guardado → valida → se valeu, telão. Senão, cria solicitação,
 * mostra QR, espera aprovação (WebSocket anônimo + polling de reserva) e troca
 * `requestId` + `deviceSecret` pelo token.
 */

const TOKEN_KEY = 'bingo.displayToken';
const SECRET_KEY = 'bingo.deviceSecret';

export interface DisplaySession {
  displayToken: string;
  eventId: string;
  eventName: string;
}

type Phase = 'checking' | 'pairing' | 'paired';

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // TV em modo privado: o pareamento vale só para esta sessão.
  }
}

function clearStored(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // nada a fazer
  }
}

export function useDisplaySession() {
  const [phase, setPhase] = useState<Phase>('checking');
  const [session, setSession] = useState<DisplaySession | null>(null);
  const [request, setRequest] = useState<DisplayRequestResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const pendingRef = useRef<{ requestId: string; deviceSecret: string } | null>(null);
  /** Tentativas seguidas de retomada que falharam por rede/servidor. */
  const retryRef = useRef(0);
  const retryTimer = useRef<number | null>(null);

  /** Troca a solicitação aprovada pelo token. */
  const exchange = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return false;

    try {
      const paired = await api<PairResponse>('/api/auth/display/requests/exchange', {
        method: 'POST',
        anonymous: true,
        body: pending,
      });

      writeStored(TOKEN_KEY, paired.displayToken);
      clearStored(SECRET_KEY);
      pendingRef.current = null;

      setSession({
        displayToken: paired.displayToken,
        eventId: paired.event.id,
        eventName: paired.event.name,
      });
      setRequest(null);
      setMessage(null);
      setPhase('paired');
      return true;
    } catch (err) {
      // "Ainda não aprovado" é o estado normal enquanto a família não mexeu no
      // celular; só o resto é problema.
      if (err instanceof ApiError && err.code === 'REQUEST_PENDING') return false;
      return false;
    }
  }, []);

  /** Cria (ou recria) a solicitação e mostra o QR. */
  const announce = useCallback(async () => {
    setPhase('pairing');

    try {
      const created = await api<DisplayRequestResponse>('/api/auth/display/requests', {
        method: 'POST',
        anonymous: true,
        body: {},
      });

      pendingRef.current = { requestId: created.requestId, deviceSecret: created.deviceSecret };
      writeStored(SECRET_KEY, pendingRef.current);
      setRequest(created);
      setMessage(null);
    } catch (err) {
      setRequest(null);
      setMessage(err instanceof ApiError ? err.message : 'Não foi possível falar com o servidor.');
    }
  }, []);

  /** Valida o token guardado e renova se estiver perto de expirar. */
  const restore = useCallback(async () => {
    const token = readStored<string>(TOKEN_KEY);
    if (!token) {
      await announce();
      return;
    }

    try {
      const me = await api<{
        event: { id: string; name: string };
        shouldRenew: boolean;
      }>('/api/display/me', { displayToken: token });

      let active = token;

      // Renovação deslizante: a TV fica semanas ligada e não pode morrer no
      // meio de um evento.
      if (me.shouldRenew) {
        const renewed = await api<{ displayToken: string }>('/api/display/renew', {
          method: 'POST',
          displayToken: token,
        });
        active = renewed.displayToken;
        writeStored(TOKEN_KEY, active);
      }

      retryRef.current = 0;
      setSession({ displayToken: active, eventId: me.event.id, eventName: me.event.name });
      setPhase('paired');
    } catch (err) {
      /**
       * Só joga o pareamento fora quando o SERVIDOR diz que ele não vale
       * mais. Rede fora, API reiniciando, 502 do proxy: nada disso é motivo
       * para apagar um token de 30 dias.
       *
       * O cenário que isto protege é o normal, não o exótico: a TV liga
       * junto com o resto da casa e o Wi-Fi ainda não subiu. Apagando o
       * token ali, o telão pedia um celular para parear de novo — por causa
       * de três segundos de rede.
       */
      const recusado =
        err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 429;

      if (!recusado) {
        retryRef.current += 1;
        setMessage('Sem conexão com o servidor. Tentando de novo…');

        if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
        retryTimer.current = window.setTimeout(
          () => void restoreRef.current?.(),
          reconnectDelayMs(retryRef.current),
        );
        return;
      }

      retryRef.current = 0;
      clearStored(TOKEN_KEY);

      await announce();

      // Depois do `announce`, que limpa o aviso ao mostrar o QR novo: quem
      // está no sofá precisa ler POR QUE a TV voltou a pedir pareamento.
      if (err.code === 'DISPLAY_EXPIRED' || err.code === 'DISPLAY_REVOKED') {
        setMessage('Este telão foi desconectado.');
      }
    }
  }, [announce]);

  /** A retentativa agendada precisa da versão mais recente de `restore`. */
  const restoreRef = useRef<typeof restore | null>(null);
  restoreRef.current = restore;

  useEffect(() => {
    void restore();

    return () => {
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    };
  }, [restore]);

  /** Espera a aprovação: WebSocket anônimo, com polling de reserva. */
  useEffect(() => {
    if (phase !== 'pairing' || !request) return;

    const connection = connectRealtime({
      query: { requestId: request.requestId },
      handlers: {
        onMessage: (incoming) => {
          if (incoming.type === 'pairing:approved') void exchange();
        },
      },
    });

    // Reserva: se o WebSocket não subir (proxy antigo, rede de hotel), o
    // pareamento ainda funciona.
    const poll = window.setInterval(() => {
      void api<{ status: string }>(`/api/auth/display/requests/${request.requestId}`, {
        anonymous: true,
      })
        .then((status) => {
          if (status.status === 'APPROVED') void exchange();
          // Expirou: a TV se reanuncia sozinha, sem ninguém levantar do sofá.
          if (status.status === 'EXPIRED') void announce();
        })
        .catch(() => undefined);
    }, 2000);

    return () => {
      connection.close();
      window.clearInterval(poll);
    };
  }, [phase, request, exchange, announce]);

  /** Chamado quando o token é rejeitado já no telão (revogação ao vivo). */
  const invalidate = useCallback(
    (reason: string) => {
      clearStored(TOKEN_KEY);
      setSession(null);
      setMessage(reason);
      void announce();
    },
    [announce],
  );

  return { phase, session, request, message, announce, invalidate };
}
